import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import CalendarHeader from './CalendarHeader';
import CalendarGrid from './CalendarGrid';
import { isValidDate } from './calendar.utils';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { DailySummaryMap, LabDashboardEventSummary } from '@/src/types/LabDashboard';

export interface LabCalendarProps {
  /** Initial month to display. Defaults to current month if omitted or invalid. */
  initialMonth?: Date;
  /** Called when the user navigates to a different month. */
  onMonthChange?: (newMonth: Date) => void;
  /** Render function for custom day content. Receives the cell's date and whether it's in the current month, returns ReactNode or null. */
  renderDayContent?: (date: Date, isCurrentMonth: boolean) => React.ReactNode | null;
  dailySummary: DailySummaryMap;
  calendarEvents: LabDashboardEventSummary[];
}

export const MONTH_KEYS = [
  i18nKeys.labDashboard.calendar.months.january,
  i18nKeys.labDashboard.calendar.months.february,
  i18nKeys.labDashboard.calendar.months.march,
  i18nKeys.labDashboard.calendar.months.april,
  i18nKeys.labDashboard.calendar.months.may,
  i18nKeys.labDashboard.calendar.months.june,
  i18nKeys.labDashboard.calendar.months.july,
  i18nKeys.labDashboard.calendar.months.august,
  i18nKeys.labDashboard.calendar.months.september,
  i18nKeys.labDashboard.calendar.months.october,
  i18nKeys.labDashboard.calendar.months.november,
  i18nKeys.labDashboard.calendar.months.december,
];

/**
 * Resolves the initial displayed month from the prop.
 * Falls back to current month if prop is undefined, null, or an invalid Date.
 */
const getInitialMonth = (initialMonth?: Date): { year: number; month: number } => {
  if (initialMonth && isValidDate(initialMonth)) {
    return { year: initialMonth.getFullYear(), month: initialMonth.getMonth() };
  }
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
};

/**
 * Top-level calendar component for the Lab Dashboard.
 * Manages displayed month state, orchestrates header and grid sub-components.
 * Does not require any context providers.
 */
const LabCalendar: React.FC<LabCalendarProps> = ({
  initialMonth,
  onMonthChange,
  renderDayContent,
  dailySummary,
  calendarEvents,
}) => {
  const { t } = useTranslation();
  const [displayedMonth, setDisplayedMonth] = useState(() => getInitialMonth(initialMonth));

  const handleToday = () => {
    const now = new Date();
    const newYear = now.getFullYear();
    const newMonth = now.getMonth();
    setDisplayedMonth({ year: newYear, month: newMonth });
    onMonthChange?.(new Date(newYear, newMonth, 1));
  };

  const handleMonthSelect = (year: number, month: number) => {
    setDisplayedMonth({ year, month });
    onMonthChange?.(new Date(year, month, 1));
  };

  const displayedMonthDate = new Date(displayedMonth.year, displayedMonth.month, 1);
  const translatedMonth = t(MONTH_KEYS[displayedMonth.month]);
  const monthYearLabel = `${translatedMonth} ${displayedMonth.year}`;

  return (
    <SpaceBetween direction="vertical" size="s">
      <CalendarHeader
        displayedMonth={displayedMonthDate}
        monthYearLabel={monthYearLabel}
        onToday={handleToday}
        onMonthSelect={handleMonthSelect}
      />
      <div aria-label={t(i18nKeys.labDashboard.calendar.calendarLabel, { monthYear: monthYearLabel })}>
        <CalendarGrid
          displayedMonth={displayedMonthDate}
          renderDayContent={renderDayContent}
          dailySummary={dailySummary}
          calendarEvents={calendarEvents}
        />
      </div>
    </SpaceBetween>
  );
};

export default LabCalendar;
